import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
	AnaOutreachOfferPrices,
	AnaOutreachReview,
	AnaOutreachReviewRow,
	smiUrl
} from 'components';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

/** What a send of the reviewed proposals answers with. */
export interface OutreachProposalsSent {
	campaign_id: string;
	outcomes: Record<string, unknown>[];
}

/**
 * The outreach review Ana's card reads and acts on, under
 * campaigns/{id}/outreach-review (T-3013).
 *
 * An interface plus a mock double, so the card can be driven in the mock build.
 */
export interface IOutreachReviewService {
	get(campaignId: string): Observable<AnaOutreachReview>;
	// CONTRACT: conversationId is the Ana conversation the card is drawn in; the server records the act there so her next turn knows it happened.
	// RAISES: 409 message_already_on_its_way — the sender has claimed the message.
	edit(
		campaignId: string,
		memberId: string,
		subject: string | null,
		body: string,
		conversationId?: string | null
	): Observable<AnaOutreachReviewRow>;
	// CONTRACT: proposal mode only. Saved as the creator's DRAFT offer; nothing is sent.
	// RAISES: 409 offer_already_sent — the offer went before the prices were saved.
	priceOffer(
		campaignId: string,
		prices: AnaOutreachOfferPrices,
		conversationId?: string | null
	): Observable<AnaOutreachReviewRow>;
	// RAISES: 409 — nothing to start, or the department refused (its detail says why).
	start(
		campaignId: string,
		conversationId?: string | null
	): Observable<AnaOutreachReview>;
	// RAISES: 409 offers_changed_since_review — the total moved since the card read it.
	sendProposals(
		campaignId: string,
		expectedTotal: string | null,
		conversationId?: string | null
	): Observable<OutreachProposalsSent>;
}

@Injectable({ providedIn: 'root' })
export class OutreachReviewService implements IOutreachReviewService {
	constructor(private http: HttpClient) {}

	get(campaignId: string): Observable<AnaOutreachReview> {
		return this.http
			.get<AnaOutreachReview>(this.base(campaignId).done())
			.pipe(
				map(review => ({
					...review,
					rows: (review.rows ?? []).map(withSkipFields)
				}))
			);
	}

	edit(
		campaignId: string,
		memberId: string,
		subject: string | null,
		body: string,
		conversationId?: string | null
	): Observable<AnaOutreachReviewRow> {
		return this.http
			.put<AnaOutreachReviewRow>(
				this.base(campaignId).add(memberId).done(),
				{ subject, body },
				{ params: conversationParams(conversationId) }
			)
			.pipe(map(withSkipFields));
	}

	priceOffer(
		campaignId: string,
		prices: AnaOutreachOfferPrices,
		conversationId?: string | null
	): Observable<AnaOutreachReviewRow> {
		return this.http
			.put<AnaOutreachReviewRow>(
				this.base(campaignId).add(prices.memberId).add('offer').done(),
				{ lines: prices.lines },
				{ params: conversationParams(conversationId) }
			)
			.pipe(map(withSkipFields));
	}

	start(
		campaignId: string,
		conversationId?: string | null
	): Observable<AnaOutreachReview> {
		return this.http.post<AnaOutreachReview>(
			this.base(campaignId).add('start').done(),
			{},
			{ params: conversationParams(conversationId) }
		);
	}

	sendProposals(
		campaignId: string,
		expectedTotal: string | null,
		conversationId?: string | null
	): Observable<OutreachProposalsSent> {
		return this.http.post<OutreachProposalsSent>(
			this.base(campaignId).add('send-proposals').done(),
			{ expected_total: expectedTotal },
			{ params: conversationParams(conversationId) }
		);
	}

	private base(campaignId: string) {
		return smiUrl().add('campaigns').add(campaignId).add('outreach-review');
	}
}

function conversationParams(
	conversationId: string | null | undefined
): Record<string, string> {
	return conversationId ? { conversation_id: conversationId } : {};
}

/**
 * The row as the card reads it: a server that predates the skip reasons and
 * the follower count sends none of those fields, which reads as "not skipped".
 */
function withSkipFields(row: AnaOutreachReviewRow): AnaOutreachReviewRow {
	return {
		...row,
		followers: row.followers ?? null,
		reason: row.reason ?? null,
		reason_detail: row.reason_detail ?? null,
		reason_at: row.reason_at ?? null
	};
}
